// ============================================================
// 11. LoyaltySummaryPage.tsx
// ============================================================
// src/features/reports/pages/LoyaltySummaryPage.tsx

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { getLoyaltySummary, getExportUrl } from '../services/reportsApi';
import { ReportHeader } from '../components/ReportHeader';
import { SummaryCard } from '../components/SummaryCard';
import {
    Star, Users, Gift, TrendingUp,
    Crown, Award, Medal, Sparkles,
    Target, ArrowUpRight
} from 'lucide-react';
import { motion } from 'framer-motion';

const tierConfig: Record<string, { icon: React.ElementType; gradient: string; color: string; label: string }> = {
    platinum: { icon: Crown, gradient: 'from-slate-300 to-slate-400', color: 'bg-slate-100 text-slate-700 border-slate-300', label: 'Platinum' },
    gold: { icon: Award, gradient: 'from-amber-400 to-yellow-500', color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Gold' },
    silver: { icon: Medal, gradient: 'from-slate-300 to-slate-400', color: 'bg-slate-50 text-slate-600 border-slate-200', label: 'Silver' },
    bronze: { icon: Star, gradient: 'from-amber-600 to-orange-700', color: 'bg-orange-50 text-orange-700 border-orange-200', label: 'Bronze' },
};

export function LoyaltySummaryPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';

    const [showFilters, setShowFilters] = useState(false);

    const { data, isLoading } = useQuery({
        queryKey: ['report-loyalty-summary', tenantId],
        queryFn: () => getLoyaltySummary(tenantId),
        enabled: !!tenantId,
    });

    const formatNaira = (val: string | number) =>
        `₦${parseFloat(String(val || '0')).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    const formatNumber = (val: string | number) => parseFloat(String(val || '0')).toLocaleString();

    const handleExport = (format: 'pdf' | 'excel') => {
        const url = getExportUrl(tenantId, 'loyalty_summary', format, {});
        window.open(url, '_blank');
    };

    const tiers = data?.tiers || [];
    const topMembers = data?.topMembers || [];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-yellow-50/30">
            <ReportHeader
                title="Loyalty Program Summary"
                description="Customer loyalty analytics"
                icon={Star}
                iconGradient="from-yellow-500 to-amber-600"
                hasFilters={false}
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(!showFilters)}
                onExportPdf={() => handleExport('pdf')}
                onExportExcel={() => handleExport('excel')}
            />

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {isLoading ? (
                    <div className="flex justify-center py-20"><Spinner size="xl" /></div>
                ) : !data ? (
                    <EmptyState
                        icon={<Star className="w-10 h-10" />}
                        title="No Loyalty Data"
                        description="No loyalty program data available."
                        size="lg"
                    />
                ) : (
                    <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <SummaryCard
                                label="Total Members"
                                value={String(data.summary?.totalMembers || 0)}
                                icon={Users}
                                gradient="from-yellow-500 to-amber-600"
                            />
                            <SummaryCard
                                label="Points Outstanding"
                                value={formatNumber(data.summary?.totalPointsOutstanding || 0)}
                                icon={Gift}
                                gradient="from-purple-500 to-violet-600"
                            />
                            <SummaryCard
                                label="Lifetime Spent"
                                value={formatNaira(data.summary?.totalLifetimeSpent || 0)}
                                icon={TrendingUp}
                                gradient="from-emerald-500 to-teal-600"
                            />
                            <SummaryCard
                                label="Avg Points/Member"
                                value={formatNumber(
                                    data.summary?.totalMembers > 0
                                        ? parseFloat(data.summary?.totalPointsOutstanding || '0') / data.summary.totalMembers
                                        : 0
                                )}
                                icon={Target}
                                gradient="from-blue-500 to-indigo-600"
                            />
                        </div>

                        {/* Tier Distribution */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            {tiers.map((tier: any, i: number) => {
                                const config = tierConfig[tier.tier] || tierConfig.bronze;
                                const TierIcon = config.icon;

                                return (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: i * 0.1, type: 'spring' }}
                                    >
                                        <Card className={`border-2 ${tier.tier === 'platinum' ? 'border-slate-300 shadow-lg' : ''}`}>
                                            <CardContent className="p-5 text-center">
                                                <div className={`inline-flex p-3 rounded-2xl bg-gradient-to-br ${config.gradient} mb-3`}>
                                                    <TierIcon className="w-6 h-6 text-white" />
                                                </div>
                                                <h3 className="text-lg font-bold text-slate-900">{config.label}</h3>
                                                <p className="text-3xl font-black text-slate-900 mt-2">{tier.count}</p>
                                                <p className="text-xs text-slate-500 mt-1">members</p>
                                                <div className="mt-3 pt-3 border-t border-slate-100 space-y-1 text-left">
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-slate-500">Points:</span>
                                                        <span className="font-semibold">{formatNumber(tier.totalPoints)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-slate-500">Spent:</span>
                                                        <span className="font-semibold">{formatNaira(tier.totalSpent)}</span>
                                                    </div>
                                                    <div className="flex justify-between text-xs">
                                                        <span className="text-slate-500">Visits:</span>
                                                        <span className="font-semibold">{formatNumber(tier.totalVisits)}</span>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                );
                            })}
                        </div>

                        {/* Top Members Table */}
                        <Card className="overflow-hidden">
                            <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50">
                                <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-amber-500" />
                                    Top Loyalty Members
                                </h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-100">
                                    <thead>
                                        <tr className="bg-slate-50/50">
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase w-12">#</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Member</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Phone</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Tier</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Points</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Earned</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Redeemed</th>
                                            <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">Total Spent</th>
                                            <th className="px-6 py-4 text-center text-xs font-semibold text-slate-500 uppercase">Visits</th>
                                            <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">Member Since</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {topMembers.map((member: any, i: number) => {
                                            const config = tierConfig[member.loyaltyTier] || tierConfig.bronze;
                                            const TierIcon = config.icon;
                                            const redemptionRate = parseFloat(member.totalPointsEarned || '0') > 0
                                                ? (parseFloat(member.totalPointsRedeemed || '0') / parseFloat(member.totalPointsEarned) * 100).toFixed(0)
                                                : '0';

                                            return (
                                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                                    <td className="px-6 py-4">
                                                        <span className={`text-sm font-bold ${i < 3 ? 'text-amber-600' : 'text-slate-400'
                                                            }`}>
                                                            {i + 1}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center gap-3">
                                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${config.color}`}>
                                                                <span className="text-xs font-bold">
                                                                    {member.name?.charAt(0) || '?'}
                                                                </span>
                                                            </div>
                                                            <span className="text-sm font-medium text-slate-900">{member.name}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">{member.phone}</td>
                                                    <td className="px-6 py-4 text-center">
                                                        <div className="flex items-center justify-center gap-1">
                                                            <TierIcon className="w-3.5 h-3.5" />
                                                            <Badge
                                                                variant={
                                                                    member.loyaltyTier === 'platinum' ? 'premium' :
                                                                        member.loyaltyTier === 'gold' ? 'warning' :
                                                                            member.loyaltyTier === 'silver' ? 'neutral' : 'info'
                                                                }
                                                                size="sm"
                                                            >
                                                                {member.loyaltyTier}
                                                            </Badge>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-amber-600">
                                                        {formatNumber(member.loyaltyPoints)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        {formatNumber(member.totalPointsEarned)}
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm text-slate-500">
                                                        <div>
                                                            <span>{formatNumber(member.totalPointsRedeemed)}</span>
                                                            <span className="text-xs text-slate-400 ml-1">({redemptionRate}%)</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-right text-sm font-bold text-slate-900">
                                                        {formatNaira(member.totalSpent)}
                                                    </td>
                                                    <td className="px-6 py-4 text-center text-sm">
                                                        {formatNumber(member.visitCount)}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-500">
                                                        {member.memberSince ? new Date(member.memberSince).toLocaleDateString() : '-'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </Card>
                    </>
                )}
            </div>
        </div>
    );
}